import React from 'react';
import { Sparkles, Gem, Bot } from 'lucide-react';
import { CustomEmojiPlayer } from '@/components/ui/CustomEmojiPlayer';

export type UserKind = 'ai' | 'p2p' | 'nft' | 'bot' | 'app';

// Resolve user/asset kinds (ai, p2p, nft, bot, app) from any listing/auction/card item
export function getUserKind(item: any): UserKind {
    if (!item) return 'p2p';
    if (item.isNft || item.is_nft || String(item.assetType || item.asset_type).toLowerCase() === 'nft') return 'nft';
    const raw =
        item.kind ||
        item.userKind ||
        item.assetType ||
        item.asset_type ||
        item.listing_type ||
        item.source ||
        item.sellerType ||
        item.seller_type ||
        item.ownership_source ||
        item.owner_type ||
        '';
    const at = String(raw).toLowerCase();
    if (at === 'nft') return 'nft';
    if (at === 'ai') return 'ai';
    if (at === 'bot') return 'bot';
    if (at === 'app' || at === 'system' || at === 'platform' || at === 'admin') return 'app';


    // Check explicit App Username indicators (Admin / App / System only)
    if (
        item.isApp === true ||
        item.is_app === true ||
        item.is_system === true ||
        item.isSystem === true ||
        item.is_platform === true ||
        item.isPlatform === true ||
        item.is_admin_listing === true ||
        item.from_my_user === true ||
        item.ownership_source === 'app' ||
        item.seller_type === 'app' ||
        item.owner_type === 'app' ||
        String(item.seller || '').toLowerCase() === 'app' ||
        String(item.seller_name || '').toLowerCase() === 'app' ||
        String(item.seller_name || '').toLowerCase() === 'admin' ||
        String(item.seller_username || '').toLowerCase() === 'app' ||
        item.seller_id === 'admin'
    ) {
        return 'app';
    }

    // Fallback: bot usernames end with "bot" (works for older listings)
    const name = String(item.username || item.name || item.displayName || '').replace(/^@/, '').toLowerCase();
    if (name.endsWith('bot')) return 'bot';
    return 'p2p';
}

// P2P route icon (third-party transfer) — same shape as before
export const RouteTypeIcon = ({ className = "w-5 h-5" }: { className?: string }) => (
    <div className="flex items-center justify-center relative transform-gpu">
        <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className={className}
        >
            <circle cx="6" cy="19" r="3" />
            <path d="M9 19h8.5a3.5 3.5 0 0 0 0-7h-11a3.5 3.5 0 0 1 0-7H15" />
            <circle cx="18" cy="5" r="3" />
        </svg>
    </div>
);

export const UserTypeIcon = ({ kind, className = "w-5 h-5" }: { kind: UserKind; className?: string }) => {
    if (kind === 'app') {
        return (
            <div className="flex items-center justify-center relative transform-gpu">
                <CustomEmojiPlayer emojiId="113" size={18} className="w-full h-full" />
            </div>
        );
    }
    if (kind === 'nft') {
        return (
            <div className="flex items-center justify-center relative transform-gpu text-purple-400">
                <Gem className={className} />
            </div>
        );
    }
    if (kind === 'ai') {
        return (
            <div className="flex items-center justify-center relative transform-gpu text-sky-300">
                <Sparkles className={className} />
            </div>
        );
    }
    if (kind === 'bot') {
        return (
            <div className="flex items-center justify-center relative transform-gpu text-emerald-400">
                <Bot className={className} />
            </div>
        );
    }
    return <RouteTypeIcon className={className} />;
};

export default UserTypeIcon;